import {Component, EventEmitter, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {AsyncPipe} from "@angular/common";
import {CrudItemComponent} from "projects/nter-ui-kit/src/lib/webkit/components/crud-item/crud-item.component";
import {CrudTableComponent} from "projects/nter-ui-kit/src/lib/webkit/components/crud-table/crud-table.component";
import {MatButton} from "@angular/material/button";
import {MatDivider} from "@angular/material/divider";
import {MatProgressBar} from "@angular/material/progress-bar";
import {MatSidenav, MatSidenavContainer, MatSidenavContent} from "@angular/material/sidenav";
import {lastValueFrom, Subject, takeUntil} from "rxjs";
import {TableActionItem} from "projects/nter-ui-kit/src/lib/webkit/components/crud-table/crud-table.model";
import {PageHeaderComponent} from "projects/nter-ui-kit/src/lib/webkit/components/page-header/page-header.component";
import {LoadingService} from "projects/nter-ui-kit/src/lib/framework/util/service/loading.service";
import {
  GrantsRoleGroupsService
} from "projects/soko-console-web-v2/src/app/features/settings/users-and-access/grants-role-groups/grants-role-groups.service";
import {SnackbarService} from "projects/nter-ui-kit/src/lib/framework/util/service/snackbar.service";
import {UserAccessService} from "projects/nter-ui-kit/src/lib/framework/security/service/user-access.service";
import {
  RoleGrantedUser,
  UngroupedRole
} from "projects/soko-console-web-v2/src/app/features/settings/users-and-access/grants-role-groups/grants-role-groups.model";
import {RevokeGrant} from "projects/nter-ui-kit/src/lib/framework/security/model/user-access.model";

@Component({
  selector: 'app-grants-roles',
  standalone: true,
  imports: [
    // AppCoreModule,
    AsyncPipe,
    CrudItemComponent,
    CrudTableComponent,
    MatButton,
    MatDivider,
    MatProgressBar,
    MatSidenav,
    MatSidenavContainer,
    MatSidenavContent,
    PageHeaderComponent
  ],
  templateUrl: './grants-roles.component.html',
})
export class GrantsRolesComponent implements OnInit, OnDestroy {
  @Input() selectedOfferingUuid!: string;

  private _loader = inject(LoadingService);
  private _service = inject(GrantsRoleGroupsService);
  private _utils = inject(SnackbarService);
  private _clearOnDestroy$ = new Subject<void>();
  private userAccessService = inject(UserAccessService);
  protected readonly grantsModelType = RoleGrantedUser;
  protected loading$ = this._loader.loading$;
  protected ungroupedGrantedRoles: UngroupedRole[] = [];
  protected updatedRow: RoleGrantedUser | null = null;
  protected revokeGrantFromUserAction = new EventEmitter<any>();
  protected consolidateUserRolesAction = new EventEmitter<any>();

  protected readonly additionalActions: TableActionItem[] = [
    {
      icon: 'remove',
      label: 'Revoke access',
      actionDataSource: 'SelectedRow',
      enabled: "RowSelected",
      includeData: true,
      eventEmitter: this.revokeGrantFromUserAction,
      keepSelectedRowOnClick: false
    },
    {
      icon: 'file_copy_off',
      label: 'Remove duplicate grants',
      actionDataSource: 'SelectedRow',
      enabled: "RowSelected",
      includeData: true,
      eventEmitter: this.consolidateUserRolesAction,
      keepSelectedRowOnClick: true
    },
  ];

  constructor() {
    this.revokeGrantFromUserAction.subscribe((event: any) => {
      this.handleRevokeUserGrant(event as RoleGrantedUser);
    });

    this.consolidateUserRolesAction.subscribe((event: any) => {
      this.handleConsolidateUserRoles(event as RoleGrantedUser);
    });
  }

  ngOnInit(): void {
    this._service.getGrantedUsersForUngroupedRoles()
      .pipe(takeUntil(this._clearOnDestroy$))
      .subscribe((response: UngroupedRole[]) => {
        this.ungroupedGrantedRoles = response
          .sort((a, b) => (a.roleCode ?? '').localeCompare(b.roleCode ?? ''));

        this.ungroupedGrantedRoles.forEach(role => {
          role.users = role.users!.sort((a, b) => (a.userFirstname ?? '').localeCompare(b.userFirstname ?? ''));
          role.users = role.users.map(user => {return {...user, roleCode: role.roleCode, roleId: role.roleId}}
          )
        })
      });
  }


  ngOnDestroy(): void {
    this._clearOnDestroy$.next();
  }


  private handleRevokeUserGrant(roleGrantedUser: RoleGrantedUser) {
    const request: RevokeGrant = {
      grantId: roleGrantedUser.grantId!,
      userId: roleGrantedUser.userId!,
      offeringUuid: this.selectedOfferingUuid!,
      principleId: roleGrantedUser.grantPrincipleId!,
      roleId: (roleGrantedUser as any).roleId
    };

    lastValueFrom(this.userAccessService.revokeAccess([request]))
      .then(() => {
        this.ungroupedGrantedRoles = this.ungroupedGrantedRoles.map(role => {
          if (role.roleId === request.roleId) {
            return {
              ...role,
              users: role.users!.filter(user => user.grantId !== request.grantId)
            };
          } else {
            return role;
          }
        });

          this._utils.openSnackBar("User access revoked successfully");
        }
      ).catch((error: string) => {
      this._utils.openSnackBar("Error revoking user access. Please try again.");
    });
  }

  private handleConsolidateUserRoles(grantedUser: RoleGrantedUser) {
    lastValueFrom(this.userAccessService.reconcileGrants(grantedUser.userId!))
      .then(() => {
        this._utils.openSnackBar("User roles consolidated successfully");
      })
      .catch((error: string) => {
        this._utils.openSnackBar("Error consolidating user roles. Please try again.");
      });
  }
}
